import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MarketingNav, MarketingFooter } from "./layout";
import { WaitlistModal } from "@/components/waitlist-modal";

type Tier = {
  id: string;
  name: string;
  tagline: string;
  price: string;
  creditsLine?: string;
  featured?: boolean;
  features: string[];
  cta: { label: string; href?: string };
};

const TALENT_TIERS: Tier[] = [
  {
    id: "starter",
    name: "Starter",
    tagline: "For solo talent sending the first invoice.",
    price: "Free + 1.5%",
    creditsLine: "100 lead + 500 AI credits / mo",
    features: [
      "Up to 50 leads, 25 active clients",
      "1 user, 1 talent profile",
      "Stripe Connect (1.5% platform fee per payment)",
      "Invoices, deposits, and native e-signature",
      "Public booking page + event-request form",
      '"Powered by Clarik.io" attribution',
    ],
    cta: { label: "Start free" },
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "When the calendar is full and the spreadsheet is the bottleneck.",
    price: "$79 / mo",
    creditsLine: "250 lead + 1,500 AI credits / mo · 2× your first month",
    featured: true,
    features: [
      "Unlimited leads & clients",
      "Up to 3 talent profiles",
      "Master schedule (calendar + spreadsheet + list + tour views)",
      "Gmail integration + drip campaigns",
      "Twilio SMS templates",
      "0% platform fee on payments",
      "Remove Clarik attribution",
    ],
    cta: { label: "Start free" },
  },
  {
    id: "studio",
    name: "Studio",
    tagline: "Tour planning, routing sheets, and the AI lead catcher.",
    price: "$129 / mo",
    creditsLine: "500 lead + 3,000 AI credits / mo · 2× your first month",
    features: [
      "Everything in Pro",
      "Unlimited talent profiles",
      "Tour planning with route optimization & availability checks",
      "AI Lead Catcher (channel-agnostic first responder)",
      "AI Assist, Lead Qualification, screenshot-to-lead",
      "Custom brand colors on client-facing pages",
    ],
    cta: { label: "Start free" },
  },
];

const AGENCY_TIERS: Tier[] = [
  {
    id: "agency",
    name: "Agency",
    tagline: "Full white-label, dialer, and reporting suite.",
    price: "$249 / mo",
    creditsLine: "1,000 lead + 6,000 AI credits / mo",
    features: [
      "Everything in Studio",
      "Unlimited users & talent",
      "White-label custom domain, From name & wordmark",
      "Browser dialer (Twilio Voice) with two-party consent prompts",
      "Executive reports + lead-source attribution",
      "Workspace ZIP export + audit logs",
      "Role-based permissions registry",
    ],
    cta: { label: "Request access" },
  },
  {
    id: "agency-pro",
    name: "Agency Pro",
    tagline: "Multi-region operators and roster-scale ops.",
    price: "$449 / mo",
    creditsLine: "2,500 lead + 15,000 AI credits / mo",
    featured: true,
    features: [
      "Everything in Agency",
      "AI Outbound Caller with cloned voice (ElevenLabs)",
      "Voice assistant with role-scoped read/write tools",
      "Social publishing (Meta, TikTok, YouTube)",
      "Apollo.io contact lookups + Email Finder credits",
      "Per-workspace KEK in Cloud KMS",
      "Priority incident response + monthly restore tests",
    ],
    cta: { label: "Request access" },
  },
];

const FAQ = [
  {
    q: "How do lead credits and AI credits work?",
    a: "Lead credits power prospecting actions — Google Places searches, Apollo.io lookups, and Email Finder requests each consume one. AI credits cover generative features: AI Assist drafts, lead qualification, screenshot-to-lead imports, and AI Lead Catcher replies. Both pools refresh monthly; unused credits roll over for up to 30 days.",
  },
  {
    q: "What's the difference between Talent plans and Agency plans?",
    a: "Talent plans (Starter, Pro, Studio) are built for individual artists and small rep shops running their own bookings. Agency plans unlock white-label (custom domain, wordmark, From name), the browser dialer, multi-user permissions, and the executive reports suite that larger booking offices need.",
  },
  {
    q: "Why does Starter take a 1.5% platform fee?",
    a: "Starter is free to use — we only earn when you do. Once you upgrade to Pro or above, the platform fee drops to 0% and Stripe's standard processing rates are all you pay.",
  },
  {
    q: "Is there a per-user or per-seat cost?",
    a: "No. Plans are flat — add reps, talent, and admins without seat fees. Starter limits exist to keep the free path honest; everything above is unlimited users.",
  },
  {
    q: "Can I move my data out?",
    a: "Yes — every workspace can trigger a one-click ZIP export with all leads, clients, invoices, schedule entries, contracts, and attachments. Your data belongs to you.",
  },
  {
    q: "Can I top up credits mid-month?",
    a: "Yes. You can purchase additional lead or AI credit bundles at any time from your workspace billing settings. Top-ups are charged immediately and expire at the end of your current billing period.",
  },
];

export default function MarketingPricing() {
  const [audience, setAudience] = useState<"talent" | "agency">("talent");
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const tiers = useMemo(() => (audience === "talent" ? TALENT_TIERS : AGENCY_TIERS), [audience]);

  return (
    <div
      className="min-h-screen flex flex-col bg-[hsl(var(--paper))] text-[hsl(var(--ink))] dark:bg-[hsl(var(--ink))] dark:text-[hsl(var(--paper))]"
      data-testid="page-marketing-pricing"
    >
      {/* ── Founding member banner ─────────────────────────────────── */}
      <div
        className="w-full bg-[hsl(var(--ink))] text-[hsl(var(--paper))] py-2.5 px-4 text-center text-sm flex flex-wrap items-center justify-center gap-3"
        data-testid="banner-founding-member"
      >
        <span className="text-[hsl(160_60%_72%)] font-medium">
          Founding member offer — lock in launch pricing forever.
        </span>
        <span
          className="inline-block font-mono text-[10px] uppercase tracking-[0.15em] bg-[hsl(160_60%_72%)/15] text-[hsl(160_60%_72%)] border border-[hsl(160_60%_72%)/30] rounded px-2 py-0.5"
          data-testid="chip-founding-spots"
        >
          Only 10 spots per business type
        </span>
        <button
          onClick={() => setWaitlistOpen(true)}
          className="text-[hsl(160_60%_72%)] underline underline-offset-4 hover:text-white transition-colors text-sm"
          data-testid="button-banner-apply"
        >
          Apply →
        </button>
      </div>

      <MarketingNav />

      <main className="flex-1 max-w-6xl mx-auto px-6 py-20 w-full">
        <header className="text-center space-y-4 mb-12">
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--graphite))]">pricing</p>
          <h1 className="text-display text-4xl md:text-6xl">Pay for the work, not the seats.</h1>
          <p className="font-italic-display text-xl text-[hsl(var(--graphite))] max-w-2xl mx-auto">
            Start free. Add features as the calendar fills. Cancel anytime.
          </p>
        </header>

        {/* ── Audience toggle ────────────────────────────────────────── */}
        <div className="flex justify-center mb-14">
          <div
            className="inline-flex p-1 rounded-full border border-[hsl(var(--fog))] dark:border-[hsl(var(--ink-soft))] bg-[hsl(var(--fog))] dark:bg-[hsl(var(--ink-soft))]"
            role="tablist"
            data-testid="toggle-audience"
          >
            <button
              role="tab"
              aria-selected={audience === "talent"}
              onClick={() => setAudience("talent")}
              className={`px-5 py-1.5 text-sm rounded-full transition-all font-medium ${
                audience === "talent"
                  ? "bg-[hsl(var(--ink))] text-[hsl(var(--paper))] dark:bg-[hsl(var(--paper))] dark:text-[hsl(var(--ink))] shadow-sm"
                  : "text-[hsl(var(--graphite))] hover:text-[hsl(var(--ink))] dark:hover:text-[hsl(var(--paper))]"
              }`}
              data-testid="button-audience-talent"
            >
              Talent / Reps
            </button>
            <button
              role="tab"
              aria-selected={audience === "agency"}
              onClick={() => setAudience("agency")}
              className={`px-5 py-1.5 text-sm rounded-full transition-all font-medium flex items-center gap-2 ${
                audience === "agency"
                  ? "bg-[hsl(var(--ink))] text-[hsl(var(--paper))] dark:bg-[hsl(var(--paper))] dark:text-[hsl(var(--ink))] shadow-sm"
                  : "text-[hsl(var(--graphite))] hover:text-[hsl(var(--ink))] dark:hover:text-[hsl(var(--paper))]"
              }`}
              data-testid="button-audience-agency"
            >
              Agencies
              <span
                className={`font-mono text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded ${
                  audience === "agency"
                    ? "bg-[hsl(var(--paper))]/20 text-[hsl(var(--paper))] dark:bg-[hsl(var(--ink))]/20 dark:text-[hsl(var(--ink))]"
                    : "bg-[hsl(var(--ink))]/10 text-[hsl(var(--graphite))]"
                }`}
                data-testid="chip-multi-talent"
              >
                MULTI-TALENT
              </span>
            </button>
          </div>
        </div>

        {/* ── Section label ──────────────────────────────────────────── */}
        <p
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--graphite))] mb-6"
          data-testid="label-plan-section"
        >
          {audience === "talent"
            ? "TALENT PLANS / SOLO TALENT & SMALL TEAMS"
            : "AGENCY PLANS · MULTI-TALENT OPERATIONS"}
        </p>

        {/* ── Tier cards ─────────────────────────────────────────────── */}
        <div className={`grid gap-6 ${tiers.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`relative rounded-2xl p-7 flex flex-col gap-5 ${
                tier.featured
                  ? "bg-[hsl(var(--ink))] text-[hsl(var(--paper))]"
                  : "bg-[hsl(var(--paper-soft,_30_20%_98%))] dark:bg-[hsl(var(--ink-soft))] border border-[hsl(var(--fog))] dark:border-[hsl(var(--ink-soft))]"
              }`}
              data-testid={`card-tier-${tier.id}`}
            >
              {tier.featured && (
                <span
                  className="absolute top-4 right-4 font-mono text-[9px] uppercase tracking-[0.15em] bg-[hsl(var(--green))] text-[hsl(var(--paper))] rounded px-2 py-0.5"
                  data-testid={`badge-most-popular-${tier.id}`}
                >
                  MOST POPULAR
                </span>
              )}

              <div>
                <h2
                  className={`text-heading text-2xl ${tier.featured ? "text-[hsl(var(--paper))]" : ""}`}
                >
                  {tier.name}
                </h2>
                <p
                  className={`text-sm mt-1 ${
                    tier.featured ? "text-[hsl(var(--paper))]/65" : "text-[hsl(var(--graphite))]"
                  }`}
                >
                  {tier.tagline}
                </p>
              </div>

              <p
                className="font-mono text-3xl font-medium"
                data-testid={`text-tier-price-${tier.id}`}
              >
                {tier.price}
              </p>

              {tier.creditsLine && (
                <p
                  className={`text-xs font-mono ${
                    tier.featured ? "text-[hsl(var(--paper))]/55" : "text-[hsl(var(--graphite))]"
                  }`}
                  data-testid={`text-tier-credits-${tier.id}`}
                >
                  {tier.creditsLine}
                </p>
              )}

              <ul className="space-y-2 text-sm flex-1">
                {tier.features.map((f) => (
                  <li
                    key={f}
                    className={`flex gap-2 items-start before:content-['✓'] before:shrink-0 before:mt-px before:font-medium ${
                      tier.featured
                        ? "before:text-[hsl(160_60%_62%)] text-[hsl(var(--paper))]/85"
                        : "before:text-[hsl(var(--green))] text-[hsl(var(--ink))] dark:text-[hsl(var(--paper))]"
                    }`}
                  >
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {tier.cta.href ? (
                <a
                  href={tier.cta.href}
                  className={`w-full text-center py-2.5 px-4 rounded-full text-sm font-medium transition-colors border ${
                    tier.featured
                      ? "border-[hsl(var(--paper))]/25 text-[hsl(var(--paper))] hover:bg-[hsl(var(--paper))]/10"
                      : "border-[hsl(var(--fog))] dark:border-[hsl(var(--ink-soft))] text-[hsl(var(--ink))] dark:text-[hsl(var(--paper))] hover:bg-[hsl(var(--fog))] dark:hover:bg-[hsl(var(--ink-soft))]"
                  }`}
                  data-testid={`button-tier-${tier.id}-cta`}
                >
                  {tier.cta.label}
                </a>
              ) : (
                <button
                  onClick={() => setWaitlistOpen(true)}
                  className={`w-full text-center py-2.5 px-4 rounded-full text-sm font-medium transition-colors ${
                    tier.featured
                      ? "bg-[hsl(var(--green))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--green-hover,_160_45%_17%))]"
                      : "border border-[hsl(var(--fog))] dark:border-[hsl(var(--ink-soft))] text-[hsl(var(--ink))] dark:text-[hsl(var(--paper))] hover:bg-[hsl(var(--fog))] dark:hover:bg-[hsl(var(--ink-soft))]"
                  }`}
                  data-testid={`button-tier-${tier.id}-cta`}
                >
                  {tier.cta.label}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* ── Credits explainer bar (talent only) ────────────────────── */}
        {audience === "talent" && (
          <div
            className="mt-6 rounded-xl bg-[hsl(var(--fog))] dark:bg-[hsl(var(--ink-soft))] px-6 py-5 grid gap-4 sm:grid-cols-3 text-sm"
            data-testid="section-credits-explainer"
          >
            <p className="text-[hsl(var(--graphite))] dark:text-[hsl(var(--paper))]/70">
              <span className="font-medium text-[hsl(var(--ink))] dark:text-[hsl(var(--paper))]">Lead credits</span>{" "}
              power prospecting — Google Places searches, Apollo lookups, and Email Finder requests each use one.
            </p>
            <p className="text-[hsl(var(--graphite))] dark:text-[hsl(var(--paper))]/70">
              <span className="font-medium text-[hsl(var(--ink))] dark:text-[hsl(var(--paper))]">AI credits</span>{" "}
              cover generative features: AI Assist drafts, lead qualification, screenshot-to-lead, and AI Lead Catcher replies.
            </p>
            <p className="text-[hsl(var(--graphite))] dark:text-[hsl(var(--paper))]/70">
              <span className="font-medium text-[hsl(var(--ink))] dark:text-[hsl(var(--paper))]">Rollover</span>{" "}
              unused credits carry over for up to 30 days. Top up any time from billing settings.
            </p>
          </div>
        )}

        <p className="text-center text-sm text-[hsl(var(--graphite))] mt-10">
          Need a custom plan or per-region rollout?{" "}
          <a href="mailto:hello@clarik.io" className="underline underline-offset-4">
            Email us
          </a>
          .
        </p>

        {/* ── Founder quote ─────────────────────────────────────────── */}
        <section
          className="mt-20 bg-[hsl(var(--ink))] text-[hsl(var(--paper))] rounded-2xl px-8 py-14"
          data-testid="section-pricing-quote"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--paper))]/50 mb-5">
            why pricing looks like this
          </p>
          <blockquote
            className="font-italic-display text-2xl md:text-3xl leading-snug max-w-3xl"
            data-testid="text-pricing-founder-quote"
          >
            "I used to run three spreadsheets, two tools, and a prayer. Clarik replaced all of it — and the price didn't change when I added my second artist."
          </blockquote>
          <p className="mt-6 text-sm uppercase tracking-[0.18em] text-[hsl(var(--paper))]/50">
            — Josh Farley, booking manager
          </p>
        </section>

        {/* ── FAQ ─────────────────────────────────────────────────────── */}
        <section className="mt-24 max-w-3xl mx-auto" data-testid="section-pricing-faq">
          <h2 className="text-heading text-3xl mb-6 text-center">Questions everyone asks</h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQ.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-[hsl(var(--fog))] dark:border-[hsl(var(--ink-soft))]"
              >
                <AccordionTrigger
                  className="text-left text-base font-medium py-5"
                  data-testid={`faq-question-${i}`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-[hsl(var(--graphite))] text-sm leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* ── Closing CTA ───────────────────────────────────────────── */}
        <div className="text-center mt-16 space-y-3">
          <p className="text-[hsl(var(--graphite))] text-sm">No credit card required. Cancel anytime.</p>
          <Button
            size="lg"
            className="bg-[hsl(var(--ink))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--ink))]/90 dark:bg-[hsl(var(--paper))] dark:text-[hsl(var(--ink))] dark:hover:bg-[hsl(var(--paper))]/90 rounded-full px-8"
            onClick={() => setWaitlistOpen(true)}
            data-testid="button-pricing-start-free"
          >
            Start free →
          </Button>
        </div>
      </main>

      <MarketingFooter />
      <WaitlistModal open={waitlistOpen} onOpenChange={setWaitlistOpen} />
    </div>
  );
}
